import React, { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import moment from "moment";
import styles from "../styles.module.scss";
import Form, { InputField, NewSelectField } from "@components/form";
import { _selectoptionType } from "@components/form/select-field";
import { Radio, Tooltip } from "antd";
import { RadioChangeEvent } from "antd/lib/radio";
import { DataSourceHandler } from "@api/data-source-handler";
import { awsRegions } from "@pages/file-browser/ui/StorageConnectionForm";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import { infoAlert } from "@components/toastify/notify-toast";
import CustomDateTimePicker from "@components/form/date-picker";
import { isEmpty } from "lodash";
import CustomMonacoEditor from "@components/monacoEditor";
import MonacoEditor from "react-monaco-editor";
import { monaco } from "@monaco-editor/react";
import { useSelector } from "react-redux";
import { RootState } from "@store/types";

const workspaceFormSchema = yup.object({
	workspaceType: yup.string().required("This is a required field"),
	workspaceName: yup.string().required("This is a required field"),
	workspaceUrl: yup
		.string()
		.required("This is a required field"),
	npipWorkSpace: yup.boolean(),
	useDockerImage: yup.boolean(),
	isDbfsEnabled: yup.boolean(),
	volumePath: yup.string().when("isDbfsEnabled", {
		is: false,
		then: yup.string().required("This is a required field"),
	}),
	configType: yup.string(),
	patToken: yup.string().when(["configType", "workspaceType"], {
		is: (configType, workspaceType) => configType === "PAT" && workspaceType === "databricks" || workspaceType === "cloudera",
		then: yup.string().required("This is a required field"),
	}),
	// expiryTime: yup.string().when(["configType", "workspaceType"], {
	// 	is: (configType, workspaceType) => configType === "PAT" && workspaceType === "databricks",
	// 	then: yup.string().matches(
	// 		/^\d+$/,
	// 		"Only epoch time is allowed"
	// 	),
	// }),
	clientId: yup.string().when(["configType", "workspaceType"], {
		is: (configType, workspaceType) => ((configType === "ASP" || configType === "FEDERATION") && workspaceType === "databricks"),
		then: yup.string().required("This is a required field"),
	}),

	clientSecret: yup.string().when(["configType", "workspaceType"], {
		is: (configType, workspaceType) => ((configType === "ASP" || configType === "FEDERATION") && workspaceType === "databricks"),
		then: yup.string().required("This is a required field"),
	}),
	accountId: yup.string().when(["configType", "workspaceType"], {
		is: (configType, workspaceType) =>
			(configType === "FEDERATION" && workspaceType === "databricks"),
		then: (schema: any) => schema.required("Account ID is required."),
		otherwise: (schema: any) => schema.notRequired(),
	}),
	username: yup.string().when("workspaceType", {
		is: "cloudera",
		then: yup.string().required("This is a required field"),
	}),
	accessKey: yup.string().when("workspaceType", {
		is: "cloudera",
		then: yup.string().required("This is a required field"),
	}),
	secretKey: yup.string().when("workspaceType", {
		is: "cloudera",
		then: yup.string().required("This is a required field"),
	}),
	region: yup.string().when("workspaceType", {
		is: "cloudera",
		then: yup.string().required("This is a required field"),
	}),
	bucketName: yup.string().when("workspaceType", {
		is: "cloudera",
		then: yup.string().required("This is a required field"),
	}),
	cloudProvider: yup.string().when("workspaceType", {
		is: "databricks",
		then: yup
			.string()
			.required("This is a required field"),
	}),
	livyConfig: yup.string().when("workspaceType", {
       is: "livy",
	   then: yup
			.string()
			.required("This is a required field"),
	})
});

export type WorkspaceFormValues = yup.InferType<typeof workspaceFormSchema>;

export type WorkSpaceFormProps = {
	type: "UPDATE" | "ADD";
	initialValues: any;
	submitButtons?: React.ReactNode;
	onSubmit?: (arg0: WorkspaceFormValues) => any;
};

const workspaceTypeOptions = [
	{ value: "databricks", label: "Databricks" }, 
	{ value: "cloudera", label: "Cloudera" },
	{ value: "livy", label: "Livy" },
];
const cloudProviderTypeOptions = [{ value: "Aws", label: "AWS" }, { value: "Azure", label: "AZURE" }];

const placeholderContent = JSON.stringify(
	{
	  accessKey: "",
	  secretKey: "",
	  region: "",
	  serviceName: ""
	},
	null,
	2
  );

export const WorkspaceForm: React.FC<WorkSpaceFormProps> = ({
	onSubmit,
	type,
	initialValues,
	submitButtons,
}) => {
	// const [awsConnectionList,setAwsConnectionList] = useState<_selectoptionType[]>([]);
	const handleFormSubmit = (values: WorkspaceFormValues) => {
		const updatedValues = values.workspaceType === "livy"
			? { ...values, livyConfig: JSON.parse(editorContent) }
			: values;
		onSubmit && onSubmit(updatedValues);
	};

	const [configType, setConfigType] = useState(
		initialValues?.configType || "PAT"
	);
	const [npipType, setNpipType] = useState(
		initialValues?.npipWorkSpace || true
	);
	const [dockerImageType, setDockerImageType] = useState(
		type ==="UPDATE" ?initialValues?.useDockerImage : true
	);
	const [isDbfsEnabled, setIsDbfsEnabled] = useState(
		type ==="UPDATE" ?(initialValues?.isDbfsEnabled ?? true) : true
	);
	const { envVariables: Env } = useSelector((store:RootState)=> store.AccountReducer);
	const [editorContent, setEditorContent] = useState(
		JSON.stringify(
			initialValues?.livyConfig && Object.values(initialValues.livyConfig).some(value => value)
				? initialValues.livyConfig
				: JSON.parse(placeholderContent),
			null,
			2
		)
	);

	const editorRef = useRef(null);

	const handleEditorDidMount = (editor:any) => {
		editorRef.current = editor;
	
		// Add custom "Copy" action
		editor.addAction({
		  id: "copy-text",
		  label: "Copy Text",
		  run: (ed:any) => navigator.clipboard.writeText(ed.getValue())
		});
	
		// Add custom "Clear" action
		editor.addAction({
		  id: "clear-editor",
		  label: "Clear Editor",
		  run: (ed:any) => {
			ed.setValue("");
		  }
		});
	  };

	const initialValuesForForm: WorkspaceFormValues = useMemo(() => {
		return {
			workspaceType: initialValues?.workspaceType || workspaceTypeOptions[0].value,
			workspaceName: initialValues?.workspaceName || "",
			workspaceUrl: initialValues?.workspaceUrl || "",
			npipWorkSpace: initialValues?.npipWorkSpace || true,
			useDockerImage:type ==="UPDATE" ?initialValues?.useDockerImage : true,
			isDbfsEnabled:type ==="UPDATE" ?(initialValues?.isDbfsEnabled ?? true) : true,
			volumePath: initialValues?.volumePath || "",
			configType: initialValues?.configType || "PAT",
			patToken: initialValues?.patToken || "",
			expiryTime: initialValues?.expiryTime || "",
			clientId: initialValues?.servicePrincipleInfo?.clientId || "",
			clientSecret: initialValues?.servicePrincipleInfo?.clientSecret || "",
			accountId: initialValues?.servicePrincipleInfo?.accountId || "",
			username: initialValues?.username || "",
			cloudProvider: initialValues?.cloudProvider || "Azure",
			accessKey: initialValues?.accessKey || "",
			secretKey: initialValues?.secretKey || "",
			region: initialValues?.region || "",
			bucketName: initialValues?.bucketName || "",
			expiryTimeType:initialValues?.expiryTimeType || false,
			livyConfig: initialValues?.livyConfig || {
				"accessKey": "",
				"secretKey":"",
				"region":"",
				"serviceName":""
			}
		};
	}, [initialValues, type]);

	// useEffect(() => {
	// 	DataSourceHandler.GetAWSConnectionList(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, (res) => {
	// 		setAwsConnectionList(
	// 			res.sort().map((item) => {
	// 				return {
	// 					label: item.name,
	// 					value: item.id,
	// 				};
	// 			})
	// 		);
	// 	});
	// }, []);

	const initialExpiryTime = initialValues?.expiryTime ? moment(initialValues.expiryTime, "YYYY,M,D,H,m") : moment();

	const {
		value: expirytimeValue,
		setValue: setExpiryTimeValue,
	} = CustomDateTimePicker.useCustomBasicController({ 
		initialValue: initialExpiryTime
	});

	const handleSetDtValue = (newDate: moment.Moment | null,setFieldValue:any) => {
		setExpiryTimeValue(newDate);
		setFieldValue("expiryTime", newDate);
	};

	return (
		<Form
			initialValues={initialValuesForForm}
			validationSchema={workspaceFormSchema}
			onSubmit={handleFormSubmit}
			enableReinitialize
		>
			{({ _formikprops: { setFieldValue, values, errors } }) => (
				<>
					<div className={styles["workspaceForm"]}>
						<NewSelectField
							name="workspaceType"
							label="Workspace Type"
							options={workspaceTypeOptions}
							required={true}
							className="mb-0"
						/>
						{values.workspaceType === "databricks" && <div>
							<p className="config-type-text">
								Configuration Type
							</p>
							<Radio.Group
								name="configType"
								onChange={(e: RadioChangeEvent) => {
									setConfigType(e.target.value);
									setFieldValue("configType", e.target.value);
								}}
								value={configType}
							>
								<Radio value="PAT">Personal Access Token</Radio>
								<Radio value="ASP">
								 Service Principle
								</Radio>
								<Radio value="FEDERATION">
								 Federation
								</Radio>
							</Radio.Group>
						</div>}
						{values.workspaceType === "databricks" && <div>
							<div>
								<p className="config-type-text">
									Enable No Public IP
								</p>
								<Radio.Group
									name="npipWorkSpace"
									className="mb-1"
									value={npipType}
									onChange={(e: RadioChangeEvent) => {
										setNpipType(e.target.value);
										setFieldValue(
											"npipWorkSpace",
											e.target.value
										);
									}}
								>
									<Radio value={true} checked={npipType}>
										Yes
									</Radio>
									<Radio value={false} checked={npipType}>
										No
									</Radio>
								</Radio.Group>
							</div>
							<div>
								<p className="config-type-text">
									Support Docker 
								</p>
								<Radio.Group
									name="useDockerImage"
									className="mb-1"
									value={dockerImageType}
									onChange={(e: RadioChangeEvent) => {
										if(type==="ADD"){
											setDockerImageType(e.target.value);
											setFieldValue(
												"useDockerImage",
												e.target.value
											);
										}else{
											infoAlert("not allowed!")
										}	
									}}
								>
									<Radio  value={true} checked={dockerImageType}>
										Yes
									</Radio>
									<Radio  value={false} checked={dockerImageType} disabled>
										No
									</Radio>
								</Radio.Group>
							</div>
							<div>
								<div>
									<Tooltip
										placement="top"
										title="Enable if DBFS is available. If not, provide a volume path with proper access permissions."
									>
										Enable DBFS 
										<img
											src="/icons/info-fields.png"
											width="16"
											height="16"
											style={{ marginLeft: "3px" }}
											className="info__icon"
											alt=""
										/>
									</Tooltip>
								</div>
								<Radio.Group
									name="isDbfsEnabled"
									className="mb-1"
									value={isDbfsEnabled}
									onChange={(e: RadioChangeEvent) => {
										setIsDbfsEnabled(e.target.value);
										setFieldValue(
											"isDbfsEnabled",
											e.target.value
										);
									}}
								>
									<Radio  value={true} checked={isDbfsEnabled}>
										Yes
									</Radio>
									<Radio  value={false} checked={isDbfsEnabled}>
										No
									</Radio>
								</Radio.Group>
							</div>
							<ShowWhenTrue show={!isDbfsEnabled}>
								<InputField
									name="volumePath"
									label="Volume Path"
									placeholder="Add Volume Path"
									required={true}
									autoComplete="off"
								/>
							</ShowWhenTrue>
							<NewSelectField
								name="cloudProvider"
								label="Cloud Provider Type"
								options={cloudProviderTypeOptions}
								required={true}
								className="mb-0"
							/>
						</div>}
						<InputField
							name="workspaceName"
							label="Workspace Name"
							placeholder="Add Workspace Name"
							required={true}
							autoComplete="off"
						/>
						<InputField
							name="workspaceUrl"
							label="Workspace URL"
							placeholder="Add URL"
							required={true}
							autoComplete="off"
						/>
						{configType === "PAT" && values.workspaceType === "databricks" && (
							<>
								<InputField
									name="patToken"
									label="PAT Token / Password"
									className="secure_input_field"
									required={true}
									isSecureInput
								/>
								{type=='UPDATE'?<span style={{color:'red'}}>Note: Please update the clusters if there are any single-user clusters.</span>:""}
								<div>
									<p className="config-type-text">
									Expiry Time
									</p>
									<Radio.Group
										name="expiryTimeType"
										className="mb-1"
										value={values?.expiryTimeType}
										onChange={(e: RadioChangeEvent) => {
											setFieldValue("expiryTimeType", e.target.value);
										}}
									>
										<Radio  value={true} checked={values?.expiryTimeType}>
											Yes
										</Radio>
										<Radio   value={false} checked={values?.expiryTimeType}>
											Never Expire
										</Radio>
									</Radio.Group>
								</div>
								<ShowWhenTrue show={values.expiryTimeType}>
									{/* <InputField
										name="expiryTime"
										label="Expiry Time"
										placeholder="Add Expiry Time"
										required={false}
									/> */}
									<CustomDateTimePicker
										label=""
										value={expirytimeValue}
										type="basic"
										setValue={(newDate)=>handleSetDtValue(newDate,setFieldValue)}
										disablePastDates
										showTime
									/>
								</ShowWhenTrue>
							</>
						)}
						{(configType === "ASP" || configType === "FEDERATION") && values.workspaceType === "databricks" && (
							<>
								<InputField
									name="clientId"
									label="Client Id"
									required={true}
									autoComplete="off"
								/>
								<InputField
									name="clientSecret"
									className="secure_input_field"
									label="Client Secret"
									isSecureInput
									required={true}
									autoComplete="off"
								/>
								<InputField
									name="accountId"
									label="Account Id"
									autoComplete="off"
								/>
							</>
						)}

						{values.workspaceType === "cloudera" && (
							<>
								<InputField
									name="patToken"
									label="PAT Token / Password"
									className="secure_input_field"
									required={true}
									isSecureInput
								/>
								<InputField
									name="username"
									label="User Name"
									required={true}
									autoComplete="off"
								/>
								<NewSelectField
									name="region"
									label="Region"
									options={awsRegions}
									required={true}
									className="mb-0"
								/>
								<InputField
									name="accessKey"
									className="secure_input_field"
									label={"Access Key"}
									required={true}
									autoComplete="off"
									isSecureInput
								/>
								<InputField
									name="secretKey"
									className="secure_input_field"
									label={"Secret Key"}
									required={true}
									autoComplete="off"
									isSecureInput
								/>
								<InputField
									name="bucketName"
									label={"Bucket Name"}
									required={true}
									autoComplete="off"
								/>
							</>
						)}

                         {values.workspaceType === "livy" && (
							<>
								{/* <MonacoEditor
									height={250}
									width="100%"
									language="json"
									value={editorContent}
									// options={
									// 	["Copy", "Clear"]
									// }
									// label="Livy Config"
									onChange={(value: any) => setEditorContent(value)}
									// required
								/> */}
								<label htmlFor="monaco-editor">
									Livy Config <span style={{ color: "red" }}>*</span>
								</label>
								<MonacoEditor
									height="250"
									width="100%"
									language="json"
									theme='vs-dark'
									defaultValue={editorContent}
									onChange={(value) => setEditorContent(value || placeholderContent)}
									editorDidMount={handleEditorDidMount}
									options={{
										minimap: { enabled: false },
										lineNumbers: "on",
										renderLineHighlight: "none",
										scrollBeyondLastLine: false,
										readOnly: false,
									}}
									
								/>
								
							</>
						)}
					</div>
					{!!submitButtons && submitButtons}
				</>
			)
			}
		</Form>
	);
};
